import React, { useState } from 'react';
import { auth, db } from './firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Hardcoded Super Admin credentials (bypass Firebase Auth)
  const SUPERADMIN_EMAIL = 'newsuperadmin@gmail.com';
  const SUPERADMIN_PASSWORD = '111222';

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate that both email and password are provided
    if (!email || !password) {
      setError('Iltimos, email va parolni kiriting.');
      setLoading(false);
      return;
    }

    try {
      const normalizedEmail = email.toLowerCase().trim();

      // 1. Check for hardcoded super admin login (bypass Firebase)
      if (!isRegistering && normalizedEmail === SUPERADMIN_EMAIL && password === SUPERADMIN_PASSWORD) {
        console.log('Super Admin login bypassed Firebase Auth');
        // Store superadmin role in localStorage (no location filter for superadmin)
        localStorage.setItem('userRole', 'superadmin');
        localStorage.removeItem('adminLocation'); // Clear location for superadmin
        const fakeUser = {
          email: SUPERADMIN_EMAIL,
          uid: 'superadmin-local-bypass'
        };
        onLogin(fakeUser, 'superadmin');
        navigate('/');
        return;
      }

      // 2. Check Firestore newadmins collection for admin login
      if (!isRegistering) {
        const adminsSnapshot = await getDocs(collection(db, 'newadmins'));
        let foundAdmin = null;
        adminsSnapshot.forEach((doc) => {
          const adminData = doc.data();
          if (adminData.email === normalizedEmail && adminData.password === password) {
            foundAdmin = { id: doc.id, ...adminData };
          }
        });

        if (foundAdmin) {
          console.log('Admin login from Firestore:', foundAdmin.email, 'Location:', foundAdmin.location);
          // Store admin location and role in localStorage for filtering
          if (foundAdmin.location) {
            localStorage.setItem('adminLocation', foundAdmin.location);
            console.log('✅ Stored admin location in localStorage:', foundAdmin.location);
          } else {
            localStorage.removeItem('adminLocation');
            console.warn('⚠️ Admin has no location set');
          }
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem('userEmail', foundAdmin.email); // Store email for fallback lookup
          const fakeUser = {
            email: foundAdmin.email,
            uid: foundAdmin.id,
            location: foundAdmin.location,
            telegramChatId: foundAdmin.telegramChatId
          };
          onLogin(fakeUser, 'admin', foundAdmin.location, foundAdmin.telegramChatId);
          navigate('/');
          return;
        } else {
          // Admin not found or wrong password
          setError('Email yoki parol noto\'g\'ri.');
          setLoading(false);
          return;
        }
      }

      // 3. Fallback to Firebase Auth (for registration or other cases)
      let userCredential;
      if (isRegistering) {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        onLogin(userCredential.user, 'admin');
        navigate('/');
      } else {
        // If not found in Firestore, show error
        setError('Email yoki parol noto\'g\'ri.');
      }
    } catch (err) {
      console.error('Auth error details:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      let friendlyError = 'Xatolik yuz berdi.';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        friendlyError = 'Email yoki parol noto\'g\'ri.';
      } else if (err.code === 'auth/email-already-in-use') {
        friendlyError = 'Bu email band. Iltimos login qiling.';
      } else if (err.code === 'auth/operation-not-allowed') {
        friendlyError = 'Firebase Console\'da Email/Parol metodi yoqilmagan.';
      } else if (err.code === 'auth/weak-password') {
        friendlyError = 'Parol juda kuchsiz (kamida 6 ta belgi).';
      } else {
        friendlyError = `Xatolik: ${err.message}`;
      }

      setError(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    .login-container {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #f6f8fa;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
      color: #24292e;
    }

    .login-card {
      background: #ffffff;
      border: 1px solid #d1d5da;
      border-radius: 6px;
      padding: 32px;
      width: 340px;
      box-shadow: 0 1px 3px rgba(27,31,35,0.12);
    }

    .login-header h1 {
      font-size: 24px;
      font-weight: 300;
      letter-spacing: -0.5px;
      margin-bottom: 24px;
      text-align: center;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      font-size: 14px;
    }

    .form-group input {
      width: 100%;
      padding: 6px 12px;
      font-size: 14px;
      line-height: 20px;
      color: #24292e;
      background-color: #ffffff;
      border: 1px solid #d1d5da;
      border-radius: 6px;
      outline: none;
      box-sizing: border-box;
    }

    .form-group input:focus {
      border-color: #0366d6;
      box-shadow: 0 0 0 3px rgba(3,102,214,0.3);
    }

    .login-button {
      width: 100%;
      padding: 6px 16px;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #ffffff;
      background-color: #2ea44f;
      border: 1px solid rgba(27,31,35,0.15);
      border-radius: 6px;
      cursor: pointer;
      margin-top: 16px;
    }

    .login-button:hover {
      background-color: #2c974b;
    }

    .login-button:disabled {
      background-color: #94d3a2;
      cursor: not-allowed;
    }

    .error-message {
      padding: 16px;
      margin-bottom: 16px;
      font-size: 13px;
      color: #721c24;
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 6px;
    }

    .toggle-auth {
      text-align: center;
      margin-top: 16px;
      border: 1px solid #d1d5da;
      border-radius: 6px;
      padding: 16px;
      width: 340px;
      background: white;
      font-size: 14px;
    }

    .toggle-auth span {
      color: #0366d6;
      cursor: pointer;
      font-weight: 500;
    }

    .login-info {
      margin-top: 24px;
      font-size: 12px;
      color: #586069;
      text-align: center;
      max-width: 340px;
    }
  `;

  return (
    <div className="login-container">
      <style>{styles}</style>
      <h1>{isRegistering ? 'Admin Ro\'yxatdan o\'tish' : 'Tizimga Kirish'}</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="login-card">
        <form onSubmit={handleAuth}>
          <div className="form-group">
            <label>Email Manzili</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Parol</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Yuklanmoqda...' : (isRegistering ? 'Ro\'yxatdan o\'tish' : 'Kirish')}
          </button>
        </form>
      </div>




    </div>
  );
};

export default Login;
